import React, { useState } from "react";
import BottomNav from "../components/BottomNav";
import { getBank } from "../data/banks";
import { usePulpayConfig } from "../hooks/usePulpayConfig";
import {
  Menu,
  Bell,
  FileText,
  ClipboardList,
  Wallet,
  HeadphonesIcon,
  Check,
  Zap,
  ShieldCheck,
  FileCheck,
  Clock,
  ArrowRight,
  Sparkles,
  CreditCard,
  Percent,
  TrendingUp,
  X,
} from "lucide-react";

export default function Home({ go, notify, user, onKreditAl, approvedCredit, hasApprovedCredit, activeCard, onActivateCredit, onTakeCredit, notifsHistory = [], onMarkNotifsRead, onClearNotifs }) {
  const displayName = user?.firstName || null;
  const [showNotifs, setShowNotifs] = useState(false);
  const [showSupport, setShowSupport] = useState(false);
  const unreadCount = notifsHistory.filter((n) => n.unread).length;
  const config = usePulpayConfig();
  const bankLogos = config.bank_logos || {};
  const support = config.support || {};

  const openNotifs = () => {
    setShowNotifs(true);
    if (unreadCount > 0 && onMarkNotifsRead) {
      setTimeout(() => onMarkNotifsRead(), 300);
    }
  };

  const handleApply = () => {
    if (hasApprovedCredit && typeof onKreditAl === "function") {
      // block re-application - handled at parent via onKreditAl guard
      onKreditAl();
      return;
    }
    onKreditAl && onKreditAl();
  };

  return (
    <section className="screen hm-screen">
      <div className="hm-bg-orb hm-bg-orb-1" />
      <div className="hm-bg-orb hm-bg-orb-2" />

      {/* Custom topbar */}
      <div className="hm-topbar">
        <div className="hm-brand">
          <img src="/assets/logo.png" alt="PULPAY" />
          <div>
            <div className="hm-brand-name">PULPAY</div>
            <div className="hm-brand-sub">KREDİT</div>
          </div>
        </div>
        <button
          className="hm-icon-btn hm-bell"
          onClick={openNotifs}
          aria-label="Bildirişlər"
          data-testid="home-bell-btn"
        >
          <Bell size={20} />
          {unreadCount > 0 && (
            <span className="hm-bell-badge" data-testid="home-bell-badge">
              {unreadCount > 9 ? "9+" : unreadCount}
            </span>
          )}
          {unreadCount > 0 && <span className="hm-bell-dot" />}
        </button>
      </div>

      <div className="content hm-content">
        {displayName && (
          <div className="hm-greet">
            <span className="hm-greet-hi">Salam</span>
            <b>{displayName}</b>
            <span className="hm-wave">👋</span>
          </div>
        )}

        {/* PULPAY Bank Card - three modes: not approved / approved without card / approved + card bound */}
        {approvedCredit && activeCard ? (
          (() => {
            const bank = getBank(activeCard.bankKey) || { name: "Bank", short: "BK", primary: "#2a55a8", secondary: "#0b2664" };
            return (
              <div
                className="hm-bcard"
                data-testid="pulpay-bank-card"
                style={{ "--bank-primary": bank.primary, "--bank-secondary": bank.secondary }}
              >
                <div className="hm-bcard-shine" />
                <div className="hm-bcard-top">
                  <div className="hm-bcard-bank">
                    <div className="hm-bcard-logo">
                      {bankLogos[activeCard.bankKey] ? (
                        <img src={bankLogos[activeCard.bankKey]} alt={bank.name} onError={(e) => { e.currentTarget.style.display = "none"; }} />
                      ) : (
                        <span>{bank.short}</span>
                      )}
                    </div>
                    <div className="hm-bcard-bank-txt">
                      <b>{bank.name}</b>
                      <span>Aktiv kredit</span>
                    </div>
                  </div>
                  <span className="hm-card-status" data-testid="card-status-approved">
                    <Check size={12} strokeWidth={3} /> Təsdiqləndi
                  </span>
                </div>

                <div className="hm-bcard-mid">
                  <span className="hm-card-label">Kredit məbləği</span>
                  <div className="hm-bcard-amount" data-testid="bcard-amount">
                    {approvedCredit.amount.toLocaleString("az-AZ")} <em>₼</em>
                  </div>
                </div>

                <div className="hm-bcard-number-wrap">
                  <div className="hm-bcard-number" data-testid="bcard-number">
                    {(activeCard.cardNumber || "").replace(/(.{4})/g, "$1 ").trim()}
                  </div>
                  <span className="hm-bcard-number-cap">Bu karta köçürüləcək</span>
                </div>

                <div className="hm-bcard-bot">
                  <div className="hm-bcard-block">
                    <span className="hm-card-lbl-sm">Aylıq</span>
                    <b data-testid="bcard-monthly">{Math.round(approvedCredit.monthly)} ₼</b>
                  </div>
                  <div className="hm-bcard-block">
                    <span className="hm-card-lbl-sm">Müddət</span>
                    <b>{approvedCredit.period} ay</b>
                  </div>
                </div>

                <button
                  className="hm-card-cta hm-card-cta-take"
                  onClick={() => onTakeCredit && onTakeCredit()}
                  data-testid="home-take-credit-cta"
                >
                  <span>Krediti götür</span>
                  <ArrowRight size={16} strokeWidth={3} />
                </button>
              </div>
            );
          })()
        ) : approvedCredit ? (
          <div className="hm-card hm-card-v2 hm-card-approved" data-testid="pulpay-credit-card-approved">
            <div className="hm-card-curve hm-card-curve-1" />
            <div className="hm-card-curve hm-card-curve-2" />
            <div className="hm-card-curve hm-card-curve-3" />

            <div className="hm-card-top">
              <div className="hm-card-brand hm-card-brand-v2">
                <span className="hm-card-brand-icon">
                  <img src="/assets/logo.png" alt="PULPAY" />
                </span>
                <span className="hm-card-brand-word">
                  <span className="hm-brand-pul">PUL</span><span className="hm-brand-pay">PAY</span>
                </span>
              </div>
              <span className="hm-card-status" data-testid="card-status-approved">
                <Check size={12} strokeWidth={3} /> Təsdiqləndi
              </span>
            </div>

            <div className="hm-card-mid hm-card-mid-v2">
              <span className="hm-card-label">Kredit məbləği</span>
              <div className="hm-card-amount" data-testid="card-approved-amount">
                {approvedCredit.amount.toLocaleString("az-AZ")} <em>₼</em>
              </div>
              <div className="hm-card-details hm-card-details-2">
                <div className="hm-card-detail">
                  <span>Aylıq</span>
                  <b data-testid="card-approved-monthly">{Math.round(approvedCredit.monthly)} ₼</b>
                </div>
                <div className="hm-card-detail">
                  <span>Müddət</span>
                  <b data-testid="card-approved-period">{approvedCredit.period} ay</b>
                </div>
              </div>
            </div>

            <button
              className="hm-card-cta hm-card-cta-activate"
              onClick={() => onActivateCredit && onActivateCredit()}
              data-testid="home-activate-cta"
            >
              <Zap size={14} strokeWidth={3} />
              <span>Krediti aktivləşdir</span>
              <ArrowRight size={16} strokeWidth={3} />
            </button>
          </div>
        ) : (
          <div className="hm-card hm-card-v2" data-testid="pulpay-credit-card">
            <div className="hm-card-curve hm-card-curve-1" />
            <div className="hm-card-curve hm-card-curve-2" />
            <div className="hm-card-curve hm-card-curve-3" />

            <div className="hm-card-top">
              <div className="hm-card-brand hm-card-brand-v2">
                <span className="hm-card-brand-icon">
                  <img src="/assets/logo.png" alt="PULPAY" />
                </span>
                <span className="hm-card-brand-word" data-testid="card-brand-word">
                  <span className="hm-brand-pul">PUL</span><span className="hm-brand-pay">PAY</span>
                </span>
              </div>
            </div>

            <div className="hm-card-mid hm-card-mid-v2">
              <span className="hm-card-label">Kredit limitiniz</span>
              <div className="hm-card-headline">
                <Sparkles size={16} />
                <span>Limitinizi öyrənin</span>
              </div>
            </div>

            <button className="hm-card-cta" onClick={handleApply} data-testid="home-credit-cta">
              <span>Limitini öyrən</span>
              <ArrowRight size={16} strokeWidth={3} />
            </button>
          </div>
        )}

        {/* Quick actions - premium tiles */}
        <div className="hm-quick">
          <button className="hm-tile hm-tile-blue" onClick={handleApply}>
            <span className="hm-tile-ic"><FileText size={20} strokeWidth={2.4} /></span>
            <span className="hm-tile-txt">Kredit<br />müraciəti</span>
          </button>
          <button className="hm-tile hm-tile-cyan" onClick={() => go("applications")}>
            <span className="hm-tile-ic"><ClipboardList size={20} strokeWidth={2.4} /></span>
            <span className="hm-tile-txt">Müraciət<br />larim</span>
          </button>
          <button
            className="hm-tile hm-tile-purple"
            onClick={() => notify("Ödənişlər bölməsi tezliklə aktiv olacaq")}
          >
            <span className="hm-tile-ic"><Wallet size={20} strokeWidth={2.4} /></span>
            <span className="hm-tile-txt">Ödənişlər</span>
          </button>
          <button
            className="hm-tile hm-tile-green"
            onClick={() => {
              if (support.redirect_to_fee) {
                go("fee_payment");
              } else {
                setShowSupport(true);
              }
            }}
            data-testid="home-support-btn"
          >
            <span className="hm-tile-ic"><HeadphonesIcon size={20} strokeWidth={2.4} /></span>
            <span className="hm-tile-txt">Dəstək</span>
          </button>
        </div>

        {/* Stats row */}
        <div className="hm-stats">
          <div className="hm-stat">
            <div className="hm-stat-ic"><Clock size={16} /></div>
            <div>
              <b>15 dəq</b>
              <span>Təsdiq</span>
            </div>
          </div>
          <div className="hm-stat">
            <div className="hm-stat-ic"><Percent size={16} /></div>
            <div>
              <b>10%</b>
              <span>İllik faiz</span>
            </div>
          </div>
          <div className="hm-stat">
            <div className="hm-stat-ic"><TrendingUp size={16} /></div>
            <div>
              <b>36 ay</b>
              <span>Müddət</span>
            </div>
          </div>
        </div>

        {/* Benefits - super premium redesign */}
        <div className="hm-benefits hm-benefits-v2" data-testid="home-benefits">
          <div className="hm-benefits-head">
            <span className="hm-eyebrow">ÜSTÜNLÜKLƏRİMİZ</span>
            <h3>Niyə <span className="hm-accent">PULPAY?</span></h3>
          </div>
          <div className="hm-benefits-list">
            <div className="hm-benefit hm-benefit-1" data-testid="home-benefit-1">
              <span className="hm-benefit-ic"><Wallet size={16} strokeWidth={2.6} /></span>
              <div className="hm-benefit-txt">
                <b>Rəsmi gəlir tələbi yox</b>
                <span>Arayış və iş yeri sənədi lazım deyil</span>
              </div>
              <span className="hm-benefit-arrow"><ArrowRight size={16} /></span>
            </div>
            <div className="hm-benefit hm-benefit-2" data-testid="home-benefit-2">
              <span className="hm-benefit-ic"><Check size={16} strokeWidth={3} /></span>
              <div className="hm-benefit-txt">
                <b>Keçmiş gecikmə maneə deyil</b>
                <span>Hər müraciət ayrıca dəyərləndirilir</span>
              </div>
              <span className="hm-benefit-arrow"><ArrowRight size={16} /></span>
            </div>
            <div className="hm-benefit hm-benefit-3" data-testid="home-benefit-3">
              <span className="hm-benefit-ic"><Clock size={16} strokeWidth={2.6} /></span>
              <div className="hm-benefit-txt">
                <b>15 dəqiqədə təsdiq</b>
                <span>Sürətli avtomatik yoxlama sistemi</span>
              </div>
              <span className="hm-benefit-arrow"><ArrowRight size={16} /></span>
            </div>
            <div className="hm-benefit hm-benefit-4" data-testid="home-benefit-4">
              <span className="hm-benefit-ic"><CreditCard size={16} strokeWidth={2.6} /></span>
              <div className="hm-benefit-txt">
                <b>Dərhal kart hesabınızda</b>
                <span>Saniyələr ərzində köçürmə</span>
              </div>
              <span className="hm-benefit-arrow"><ArrowRight size={16} /></span>
            </div>
          </div>
        </div>
      </div>

      <BottomNav active="home" go={go} notify={notify} />

      {/* Support platform overlay */}
      {showSupport && (
        <div
          className="hm-support-overlay"
          onClick={() => setShowSupport(false)}
          data-testid="home-support-overlay"
        >
          <div
            className="hm-support-sheet"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
          >
            <div className="hm-support-head">
              <div className="hm-support-title">
                <HeadphonesIcon size={16} />
                <span>Dəstək kanalları</span>
              </div>
              <button
                type="button"
                className="hm-notifs-close"
                onClick={() => setShowSupport(false)}
                aria-label="Bağla"
                data-testid="home-support-close"
              >
                <X size={16} strokeWidth={2.6} />
              </button>
            </div>
            <div className="hm-support-body">
              <p className="hm-support-p">7/24 onlayn olduğumuz kanallardan birini seçin.</p>
              <div className="hm-support-list">
                <SupportLink
                  href={support.whatsapp}
                  logoUrl={support.whatsapp_logo}
                  color="#25D366"
                  label="WhatsApp"
                  hint="Mesajlar dəqiqələr içində cavablanır"
                  testid="support-whatsapp"
                />
                <SupportLink
                  href={support.telegram}
                  logoUrl={support.telegram_logo}
                  color="#229ED9"
                  label="Telegram"
                  hint="Kanala qoşulun və birbaşa yazın"
                  testid="support-telegram"
                />
                <SupportLink
                  href={support.tiktok}
                  logoUrl={support.tiktok_logo}
                  color="#000000"
                  label="TikTok"
                  hint="Rəsmi hesabımızı izləyin"
                  testid="support-tiktok"
                />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Notifications overlay (bell dropdown) */}
      {showNotifs && (
        <div
          className="hm-notifs-overlay"
          onClick={() => setShowNotifs(false)}
          data-testid="home-notifs-overlay"
        >
          <div
            className="hm-notifs-panel"
            onClick={(e) => e.stopPropagation()}
            role="dialog"
            aria-modal="true"
            data-testid="home-notifs-panel"
          >
            <div className="hm-notifs-head">
              <div className="hm-notifs-title">
                <Bell size={16} />
                <span>Bildirişlər</span>
                {notifsHistory.length > 0 && (
                  <span className="hm-notifs-count">{notifsHistory.length}</span>
                )}
              </div>
              <div className="hm-notifs-actions">
                {notifsHistory.length > 0 && (
                  <button
                    type="button"
                    className="hm-notifs-clear"
                    onClick={() => onClearNotifs && onClearNotifs()}
                    data-testid="home-notifs-clear"
                  >
                    Təmizlə
                  </button>
                )}
                <button
                  type="button"
                  className="hm-notifs-close"
                  onClick={() => setShowNotifs(false)}
                  aria-label="Bağla"
                  data-testid="home-notifs-close"
                >
                  <X size={16} strokeWidth={2.6} />
                </button>
              </div>
            </div>
            <div className="hm-notifs-body">
              {notifsHistory.length === 0 ? (
                <div className="hm-notifs-empty" data-testid="home-notifs-empty">
                  <Bell size={22} />
                  <span>Hələ bildiriş yoxdur</span>
                </div>
              ) : (
                <div className="hm-notifs-items">
                  {notifsHistory.map((n) => (
                    <div
                      key={n.id}
                      className={`hm-notifs-item hm-notifs-item-${n.kind || "success"} ${n.unread ? "unread" : ""}`}
                      data-testid={`home-notifs-item-${n.id}`}
                    >
                      <div className="hm-notifs-item-logo">
                        <img src="/assets/logo.png" alt="" />
                      </div>
                      <div className="hm-notifs-item-body">
                        <div className="hm-notifs-item-head">
                          <b>{n.title}</b>
                          <span>{n.time}</span>
                        </div>
                        <div className="hm-notifs-item-title">{n.body}</div>
                        {n.sub && <div className="hm-notifs-item-sub">{n.sub}</div>}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </section>
  );
}

function BrandIcon({ label }) {
  const key = (label || "").toLowerCase();
  if (key === "whatsapp") {
    return (
      <svg viewBox="0 0 32 32" width="20" height="20" aria-hidden="true">
        <path fill="#fff" d="M16.02 4C9.94 4 5 8.94 5 15.02c0 2.13.62 4.11 1.68 5.79L5 27l6.39-1.66a11 11 0 0 0 4.63 1.02h.01c6.08 0 11.02-4.94 11.02-11.02 0-2.94-1.15-5.71-3.23-7.79A10.94 10.94 0 0 0 16.02 4Zm0 20.09h-.01a9.13 9.13 0 0 1-4.65-1.27l-.33-.2-3.79.99 1.01-3.69-.22-.38a9.09 9.09 0 0 1-1.4-4.85c0-5.03 4.09-9.12 9.13-9.12a9.12 9.12 0 0 1 9.13 9.13c0 5.03-4.09 9.39-9.12 9.39Zm5.27-6.83c-.29-.14-1.71-.84-1.97-.94-.27-.1-.46-.15-.66.15-.19.29-.75.94-.92 1.13-.17.19-.34.22-.63.07-.29-.14-1.22-.45-2.32-1.43-.86-.77-1.44-1.71-1.61-2-.17-.29-.02-.45.13-.6.13-.13.29-.34.44-.51.15-.17.19-.29.29-.49.1-.19.05-.36-.02-.51-.07-.14-.66-1.58-.9-2.17-.24-.57-.48-.5-.66-.5l-.56-.01c-.19 0-.51.07-.78.36-.27.29-1.02.99-1.02 2.42s1.05 2.81 1.2 3c.15.19 2.06 3.15 4.99 4.42.7.3 1.24.48 1.66.61.7.22 1.33.19 1.83.11.56-.08 1.71-.7 1.95-1.37.24-.68.24-1.26.17-1.37-.07-.11-.27-.19-.56-.33Z"/>
      </svg>
    );
  }
  if (key === "telegram") {
    return (
      <svg viewBox="0 0 32 32" width="20" height="20" aria-hidden="true">
        <path fill="#fff" d="M27.09 6.34 3.83 15.31c-1.4.54-1.39 1.32-.26 1.66l5.63 1.76 2.17 6.66c.26.72.13.99.89 1 .59.01.85-.28 1.17-.61l2.84-2.76 5.9 4.36c1.09.6 1.87.29 2.14-1.01l3.87-18.24c.4-1.6-.61-2.32-1.65-1.85ZM11.53 20.4l12.21-7.71c.57-.35 1.09-.16.66.23L14.14 22.36l-.36 3.85-2.25-5.81Z"/>
      </svg>
    );
  }
  if (key === "tiktok") {
    return (
      <svg viewBox="0 0 32 32" width="20" height="20" aria-hidden="true">
        <path fill="#fff" d="M21.5 4c.36 2.32 1.66 4.07 3.9 4.4v3.34c-1.42.14-2.66-.32-4.1-1.2v6.9c0 4.55-2.87 7.56-6.9 7.56A6.9 6.9 0 1 1 15.86 12h.79v3.6a3.5 3.5 0 0 0-3.66 3.4 3.5 3.5 0 0 0 3.6 3.5c1.95 0 3.35-1.34 3.35-3.43V4h1.56Z"/>
      </svg>
    );
  }
  return <span>{(label || "").slice(0, 2).toUpperCase()}</span>;
}

function SupportLink({ href, logoUrl, color, label, hint, testid }) {
  const disabled = !href;
  const Tag = disabled ? "div" : "a";
  const props = disabled ? {} : { href, target: "_blank", rel: "noopener noreferrer" };
  return (
    <Tag
      className={`hm-support-item ${disabled ? "disabled" : ""}`}
      style={{ "--sc": color }}
      data-testid={testid}
      {...props}
    >
      <span className="hm-support-badge" aria-hidden>
        {logoUrl ? (
          <img
            src={logoUrl}
            alt={label}
            style={{ width: "76%", height: "76%", objectFit: "contain" }}
            onError={(e) => { e.currentTarget.style.display = "none"; }}
          />
        ) : (
          <BrandIcon label={label} />
        )}
      </span>
      <div className="hm-support-txt">
        <b>{label}</b>
        <span>{disabled ? "Hazırlıq mərhələsindədir" : hint}</span>
      </div>
      <span className="hm-support-arrow">
        {disabled ? "—" : "→"}
      </span>
    </Tag>
  );
}
